import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PAGE_LABELS = {
  home: "General",
  pets: "Pets",
  "pet-transport": "Pet Transport",
  "cost-calculator": "Pricing & Quotes",
  book: "Booking & Calls",
  "start-here": "Getting Started",
  merida: "Mérida",
  driving: "Driving to Mexico",
  "drivers-transport": "Drivers & Transport",
  "residency-documents": "Residency & Documents",
};

const prettify = (slug) =>
  PAGE_LABELS[slug] || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Faq() {
  const [faqs, setFaqs] = useState([]);
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/faqs").then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  const groups = faqs.reduce((acc, f) => {
    const key = f.page || "home";
    (acc[key] = acc[key] || []).push(f);
    return acc;
  }, {});
  const orderedKeys = Object.keys(groups).sort((a, b) =>
    a === "home" ? -1 : b === "home" ? 1 : prettify(a).localeCompare(prettify(b))
  );

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div data-testid="page-faq">
      <Seo
        path="/faq"
        title="FAQ — Moving to Mexico questions, answered"
        description="Frequently asked questions about moving from the US to Mexico: pricing, pets, driving, residency, and how Hola Movers works."
      />
      {faqs.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>
      )}
      <section className="mx-auto max-w-3xl px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900">
          {lang === "es" ? "Preguntas frecuentes" : "Frequently asked questions"}
        </h1>
        <p className="mt-5 text-lg text-charcoal-600 leading-relaxed">
          {lang === "es"
            ? "Respuestas claras sobre precios, mascotas, manejo, residencia y cómo funciona Hola Movers."
            : "Clear answers about pricing, pets, driving, residency, and how Hola Movers works."}
        </p>

        {orderedKeys.map((key) => (
          <div key={key} className="mt-12" data-testid={`faq-group-${key}`}>
            <h2 className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-2">
              {prettify(key)}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {groups[key].map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border-b border-sand-200">
                  <AccordionTrigger
                    data-testid={`faq-page-trigger-${f.id}`}
                    className="text-left text-lg font-medium text-charcoal-800 hover:text-terracotta hover:no-underline py-5"
                  >
                    {lang === "es" && f.question_es ? f.question_es : f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-charcoal-600 pb-5">
                    {lang === "es" && f.answer_es ? f.answer_es : f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        {faqs.length === 0 && (
          <p className="mt-12 text-charcoal-500" data-testid="faq-empty">
            No FAQs published yet — check back soon.
          </p>
        )}

        <div className="mt-16 rounded-3xl bg-white border border-sand-200 p-8">
          <h3 className="font-serif text-2xl text-charcoal-800">
            {lang === "es" ? "¿Aún tienes preguntas?" : "Still have a question?"}
          </h3>
          <p className="mt-2 text-charcoal-600">
            {lang === "es"
              ? "Escríbenos — leemos cada mensaje. Sin presión, sin pago."
              : "Send us a message — we read every one. No pressure, no payment."}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="faq-contact-cta" className="rounded-full border border-charcoal-300 px-6 py-2.5 text-sm font-medium text-charcoal-800 hover:bg-sand-200 transition-colors">
              {lang === "es" ? "Contáctanos" : "Contact us"}
            </Link>
            <Link to="/cost-calculator" data-testid="faq-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover transition-colors">
              {lang === "es" ? "Cotiza ahora" : "Get a quote"} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
